import {
  faCalendarPlus,
  faChevronDown,
  faPlus,
  faTimeline
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import AppointmentTypesByPhaseList from '@/components/AppointmentTypesByPhaseList';
import EnrolledGroupsBanner from '@/components/EnrolledGroupsBanner';
import FutureAppointmentRequestForDateModal from '@/components/FutureAppointmentRequestForDateModal';
import MedicalStarterSequenceModal from '@/components/MedicalStarterSequenceModal';
import PageHeader from '@/components/PageHeader';
import PersonTreatmentWeekAndDay from '@/components/PersonTreatmentWeekAndDay';
import SendScheduleSummaryButton from '@/components/SendScheduleSummaryButton';
import StaffEnrollmentWarning from '@/components/StaffEnrollmentWarning';
import { Color } from '@/constants/color';
import defaultTimeZone from '@/constants/defaultTimeZone';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import { TREATMENT_GROUP_BOOKING_PARAM } from '@/constants/treatmentGroups';
import ScheduleExpectationStatusContext from '@/contexts/ScheduleExpectationStatusContext';
import CustomScheduleExpectationForm from '@/forms/CustomScheduleExpectationForm/CustomScheduleExpectationForm';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { useScheduleExpectationsCyclesWebsocket } from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import useStaff from '@/hooks/useStaff';
import { useStaffEnrollments } from '@/hooks/useStaffEnrollments';
import { useEnrolledTreatmentGroupsForPerson } from '@/hooks/useTreatmentGroupsForPersonInfinite';
import PersonContext from '@/pages/Person/PersonContext';
import RecomputeCyclesButton from '@/pages/Scheduling/components/RecomputeCyclesButton';
import ScheduleExpectations from '@/pages/Scheduling/components/ScheduleExpectations';
import { SchedulingSegment } from '@/pages/Scheduling/types';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import { PersonAvailability } from '@/types/personAvailability';
import { TreatmentGroupForPerson } from '@/types/treatmentGroup';
import {
  Alert,
  BetaBadge,
  Button as ButtonV2,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Pill,
  TabSwitch,
  TabSwitchList,
  TabSwitchTab,
  Tooltip,
  Typography
} from '@/ui';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import DataList, { DataRow } from '@/ui/components/DataList';
import DualTime from '@/ui/components/DualTime';
import Modal from '@/ui/components/Modal';
import { daysBetween } from '@/utils/dates';
import formatStaffName from '@/utils/formatStaffName';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

const Scheduling = () => {
  const { person } = useContext(PersonContext);
  const {
    id,
    episode_of_care,
    active_classifications,
    classifications,
    external_links: { healthie },
    first_name,
    time_zone,
    jurisdiction,
    insurance_policies,
    staff_relationships
  } = person;
  const { state } = useLocation();
  const { navigateToBooking } = useNavigateToPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [segment, setSegment] = useState<SchedulingSegment>(SchedulingSegment.SCHEDULE);
  const [expectationsAppointmentTypeId, setExpectationsAppointmentTypeId] = useState<string>('');
  const [expectationsExpectationId, setExpectationsExpectationId] = useState<string>('');
  const [isCreateExpectationModalOpen, setIsCreateExpectationModalOpen] = useState(false);
  const [isFutureAppointmentRequestModalOpen, setIsFutureAppointmentRequestModalOpen] =
    useState(false);
  const [isMedicalStarterModalOpen, setIsMedicalStarterModalOpen] = useState(false);
  const [scheduleActiveAppointmentTypeId, setScheduleActiveAppointmentTypeId] =
    useState<string>('');
  const [expectationsSelectedAppointmentTypeId, setExpectationsSelectedAppointmentTypeId] =
    useState<string>('');

  // The person toolbar's future appointment request modal navigates here on success. Clear the
  // param once consumed so the same appointment type can be highlighted twice in a row, and so
  // reloading or navigating back doesn't re-apply a stale highlight.
  useEffect(() => {
    const appointmentTypeId = searchParams.get('appointmentTypeId');
    if (!appointmentTypeId) return;

    setScheduleActiveAppointmentTypeId(appointmentTypeId);
    setSegment(SchedulingSegment.SCHEDULE);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('appointmentTypeId');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const availabilityRepo = PersonAvailabilityRepository();
  const enrolledTreatmentGroups = useEnrolledTreatmentGroupsForPerson(id, {
    timeZone: time_zone || defaultTimeZone
  });
  const { status: processingStatus, appointmentTypeIds: processingAppointmentTypeIds } =
    useScheduleExpectationsCyclesWebsocket(id);
  const { data: availabilityData } = useAPI({
    queryKey: ['availability', id, 'scheduling-metadata'],
    queryFn: async () => {
      const response = await availabilityRepo.listByPersonId({
        personId: id,
        params: { page: '1', size: '1' }
      });
      return response.data;
    }
  });

  const availability = useMemo((): PersonAvailability | null => {
    if (Array.isArray(availabilityData?.results) && availabilityData.results.length > 0) {
      return availabilityData.results[0];
    }
    return null;
  }, [availabilityData]);

  const availabilityLastUpdatedAt = useMemo(() => {
    if (!availability?.updated_at) return 'N/A';

    const updatedAtDate = new Date(availability.updated_at);
    if (Number.isNaN(updatedAtDate.getTime())) return 'N/A';

    return (
      <DualTime
        dateTime={updatedAtDate}
        memberTZ={availability.timezone || time_zone || defaultTimeZone}
      />
    );
  }, [availability?.updated_at, availability?.timezone, time_zone]);

  const availabilityLastUpdatedBy = useMemo(() => {
    if (!availability) return 'N/A';

    const updatedByName = availability.last_modified_by_name;
    const updatedByRole = availability.last_modified_by_role;

    if (!updatedByName && !updatedByRole) return 'N/A';
    if (!updatedByRole) return updatedByName || 'N/A';

    return `${updatedByName || 'Not Set'} (${updatedByRole})`;
  }, [availability]);

  const isAvailabilityStale = useMemo(
    () => classifications.includes('availability.stale'),
    [classifications]
  );

  const staleAvailabilityDescription = useMemo(() => {
    const staleClassification = active_classifications.find(
      (classification) => classification.key === 'availability.stale'
    );

    return staleClassification?.description ?? 'No description available';
  }, [active_classifications]);

  // state.providerId is the Healthie ID originated from our chrome extension
  const selectedProviderId = state?.providerId ? Number(state.providerId) : undefined;

  const { data: staffData } = useStaff({ enabled: !!selectedProviderId });
  const selectedStaff = useMemo(() => {
    if (staffData) {
      return staffData?.data?.find((staff) => staff.healthie_id === selectedProviderId);
    }
    return null;
  }, [staffData, selectedProviderId]);
  const selectedStaffName = useMemo(() => {
    if (!selectedStaff) return null;
    return formatStaffName({ staff: selectedStaff });
  }, [selectedStaff]);

  const { data: staffEnrollmentsData } = useStaffEnrollments(
    { person_id: id },
    !!selectedProviderId
  );
  const isStaffEnrolled = useMemo(() => {
    if (staffEnrollmentsData && selectedStaff) {
      return staffEnrollmentsData.some(
        (staffEnrollment) => staffEnrollment.staff_id === selectedStaff?.id
      );
    }
    return false;
  }, [staffEnrollmentsData, selectedStaff]);

  const jurisdictionName = jurisdiction?.name || 'No jurisdiction';
  const defaultPolicy = insurance_policies?.find((policy) => policy.is_default) || {};
  const defaultPolicyName = defaultPolicy.provider_name || 'No insurance';
  const defaultPolicyPlan = defaultPolicy.plan;
  const defaultPolicyType = defaultPolicy.type || 'No insurance type';
  const appointmentRestrictionReasons = defaultPolicy.appointment_restriction_reasons || [];

  const medicalProviderName = (() => {
    const medicalProvider = staff_relationships?.find(
      (relationship) => relationship.relationship_type?.slug === 'medical-provider'
    )?.staff;
    return medicalProvider ? formatStaffName({ staff: medicalProvider }) : null;
  })();

  const primaryCounselorName = (() => {
    const primaryCounselor = staff_relationships?.find(
      (relationship) => relationship.relationship_type?.slug === 'primary-counselor'
    )?.staff;
    return primaryCounselor ? formatStaffName({ staff: primaryCounselor }) : null;
  })();

  const handleSelectTreatmentGroup = (group: TreatmentGroupForPerson) => {
    const params = new URLSearchParams({ appointmentTypeId: group.appointment_type_id });
    params.set(TREATMENT_GROUP_BOOKING_PARAM, group.id);
    navigateToBooking(id, params);
  };

  const handleTabs = (newSegment: SchedulingSegment) => {
    setScheduleActiveAppointmentTypeId('');
    setSegment(newSegment);
  };

  const handleSeeExpectationDetails = (appointmentTypeId: string, expectationId: string) => {
    setExpectationsAppointmentTypeId(appointmentTypeId);
    setExpectationsExpectationId(expectationId);
    setSegment(SchedulingSegment.EXPECTATIONS);
  };

  const handleCreateExpectationSuccess = (appointmentTypeId: string) => {
    setIsCreateExpectationModalOpen(false);
    setExpectationsAppointmentTypeId(appointmentTypeId);
    setExpectationsExpectationId('');
    setSegment(SchedulingSegment.EXPECTATIONS);
  };

  const handleFutureAppointmentRequestSuccess = (appointmentTypeId: string) => {
    setScheduleActiveAppointmentTypeId(appointmentTypeId);
    setSegment(SchedulingSegment.SCHEDULE);
  };

  const handleExpectationsAppointmentTypeChange = useCallback((appointmentTypeId: string) => {
    setExpectationsSelectedAppointmentTypeId(appointmentTypeId);
  }, []);

  const formInitialAppointmentTypeId =
    segment === SchedulingSegment.EXPECTATIONS ? expectationsSelectedAppointmentTypeId : undefined;

  const renderEpisodeAlerts = () => {
    if (!episode_of_care) {
      return <Alert variant="info" message="This person has no episode of care" />;
    }
    if (!episode_of_care.current_active_phase) {
      return <Alert variant="info" message="This person has no active phase" />;
    }

    return null;
  };

  const renderMissingVerifiableBanner = () => {
    if (!selectedStaffName || !selectedStaff || isStaffEnrolled) return null;

    return (
      <StaffEnrollmentWarning
        staffId={selectedStaff.id}
        personId={id}
        staffName={selectedStaffName}
        insurance={defaultPolicyName}
        insuranceType={defaultPolicyType}
        jurisdiction={jurisdictionName}
      />
    );
  };

  const renderSegment = () => {
    switch (segment) {
      case SchedulingSegment.SCHEDULE:
        return (
          <>
            {renderEpisodeAlerts() || (
              <>
                {episode_of_care?.current_active_phase && (
                  <AppointmentTypesByPhaseList
                    activeAppointmentTypeId={scheduleActiveAppointmentTypeId}
                    onSeeExpectationDetails={handleSeeExpectationDetails}
                    phase={episode_of_care.current_active_phase}
                    timeZone={time_zone || defaultTimeZone}
                  />
                )}
              </>
            )}
          </>
        );
      case SchedulingSegment.EXPECTATIONS:
        return (
          renderEpisodeAlerts() || (
            <ScheduleExpectations
              initialAppointmentTypeId={expectationsAppointmentTypeId}
              initialExpectationId={expectationsExpectationId}
              onAppointmentTypeChange={handleExpectationsAppointmentTypeChange}
            />
          )
        );
      default:
        return null;
    }
  };

  return (
    <Container type="page">
      <PageHeader headingText="Scheduling" className="sr-only" />

      <div className="flex justify-end items-center gap-2 mb-2">
        {healthie?.url && !selectedProviderId && (
          <Button
            color={Color.INDIGO}
            href={healthie.url}
            rel="noreferrer"
            size="sm"
            target="_blank">
            Open in Healthie
          </Button>
        )}
        <SendScheduleSummaryButton personId={id} />
      </div>

      <Card>
        <DataList>
          <DataRow label="Jurisdiction" content={jurisdictionName || 'N/A'} />
          <DataRow label="Insurance">
            <div className="flex items-center gap-2">
              <Tooltip content={`Plan: ${defaultPolicyPlan}`} disabled={!defaultPolicyPlan}>
                <span>{defaultPolicyName || 'N/A'}</span>
              </Tooltip>
              <Tooltip
                content={
                  <ul className="list-disc pl-4">
                    {appointmentRestrictionReasons.map((reason) => (
                      <li key={reason.code}>{reason.description}</li>
                    ))}
                  </ul>
                }
                disabled={!appointmentRestrictionReasons.length}>
                <Pill
                  color={appointmentRestrictionReasons.length ? 'warning' : 'neutral'}
                  size="sm"
                  variant="subtle">
                  {appointmentRestrictionReasons.length ? 'Restricted' : 'Not Restricted'}
                </Pill>
              </Tooltip>
            </div>
          </DataRow>
          <DataRow label="Insurance Type" content={defaultPolicyType || 'N/A'} />
          <DataRow label="Primary Counselor" content={primaryCounselorName || 'N/A'} />
          <DataRow label="Medical Provider" content={medicalProviderName || 'N/A'} />
          <DataRow label="Admitted At">
            <PersonTreatmentWeekAndDay />
          </DataRow>
          <DataRow label="Current Phase">
            {episode_of_care?.current_active_phase ? (
              <div>
                <div>
                  {snakeCaseToTitleCase(episode_of_care.current_active_phase.phase_type.name) ||
                    'N/A'}
                </div>
                {episode_of_care.current_active_phase.start_at &&
                  (() => {
                    const startAt = new Date(episode_of_care.current_active_phase.start_at!);
                    const days = daysBetween(startAt, new Date(), true);
                    return (
                      <div>
                        <DualTime memberTZ={time_zone ?? undefined} dateTime={startAt} />{' '}
                        <strong className="text-sm">
                          {days} day{days === '1' ? '' : 's'} ago
                        </strong>
                      </div>
                    );
                  })()}
              </div>
            ) : (
              'No Active Phase'
            )}
          </DataRow>
          <DataRow label="Program">
            {episode_of_care?.current_active_phase?.program?.name || 'No Program'}
          </DataRow>
          <DataRow label="Schedule">
            {episode_of_care?.current_active_phase?.program?.schedule_name || 'No Schedule'}
          </DataRow>
          <DataRow label="Availability Last Updated">
            <div className="flex items-center gap-2">
              {availabilityLastUpdatedAt}
              {isAvailabilityStale && (
                <Tooltip content={staleAvailabilityDescription}>
                  <Badge color={Color.ORANGE} text="Stale" />
                </Tooltip>
              )}
            </div>
          </DataRow>
          <DataRow label="Availability Last Updated By" content={availabilityLastUpdatedBy} />
        </DataList>
      </Card>

      {enrolledTreatmentGroups.items.length > 0 && (
        <div className="mt-4 space-y-2">
          <Typography variant="heading">Groups</Typography>
          <EnrolledGroupsBanner
            groups={enrolledTreatmentGroups.items}
            memberFirstName={first_name || undefined}
            onSelect={handleSelectTreatmentGroup}
          />
        </div>
      )}

      {renderMissingVerifiableBanner()}

      <div className="flex justify-between items-center mb-3">
        <Typography variant="heading">Schedule & Expectations</Typography>

        <DropdownMenu>
          <DropdownMenuTrigger className="group" aria-label="Open dropdown">
            <ButtonV2 variant="outline" color="neutral" size="sm">
              Actions
              <FontAwesomeIcon
                icon={faChevronDown}
                className="ml-1.5 h-3 w-3 transition-transform duration-200 group-data-[open]:rotate-180"
              />
            </ButtonV2>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <RecomputeCyclesButton personId={id} />
            <DropdownMenuItem icon={faPlus} onClick={() => setIsCreateExpectationModalOpen(true)}>
              Create New Expectation
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              icon={faCalendarPlus}
              onClick={() => setIsFutureAppointmentRequestModalOpen(true)}>
              Submit Future Appointment Request <BetaBadge variant="superscript" />
            </DropdownMenuItem>
            <DropdownMenuItem icon={faTimeline} onClick={() => setIsMedicalStarterModalOpen(true)}>
              Create Medical Starter Sequence <BetaBadge variant="superscript" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className={`z-19 bg-gray-100 pt-1 mb-4 ${STICKY_CHROME_CLASS.pageSection}`}>
        <TabSwitch
          variant="pills"
          stretch
          selectedIndex={segment === SchedulingSegment.SCHEDULE ? 0 : 1}
          onChange={(idx) =>
            handleTabs(idx === 0 ? SchedulingSegment.SCHEDULE : SchedulingSegment.EXPECTATIONS)
          }>
          <TabSwitchList>
            <TabSwitchTab>Schedule</TabSwitchTab>
            <TabSwitchTab>Expectations</TabSwitchTab>
          </TabSwitchList>
        </TabSwitch>
      </div>

      {processingStatus !== 'idle' && (
        <div className="mt-4 mb-4">
          <Alert variant={processingStatus === 'complete' ? 'success' : 'info'}>
            {processingStatus === 'queued' && 'Schedule expectations queued for reprocessing...'}
            {processingStatus === 'processing' && 'Reprocessing schedule expectations...'}
            {processingStatus === 'complete' && 'Schedule expectations reprocessing complete.'}
          </Alert>
        </div>
      )}

      <ScheduleExpectationStatusContext.Provider
        value={{ status: processingStatus, appointmentTypeIds: processingAppointmentTypeIds }}>
        {renderSegment()}
      </ScheduleExpectationStatusContext.Provider>

      <Modal
        open={isCreateExpectationModalOpen}
        onClose={() => setIsCreateExpectationModalOpen(false)}
        title="Create New Expectation">
        {episode_of_care?.current_active_phase && (
          <CustomScheduleExpectationForm
            initialAppointmentTypeId={formInitialAppointmentTypeId}
            onCancel={() => setIsCreateExpectationModalOpen(false)}
            onSuccess={handleCreateExpectationSuccess}
            phase={episode_of_care.current_active_phase}
            timeZone={time_zone || defaultTimeZone}
          />
        )}
      </Modal>

      <FutureAppointmentRequestForDateModal
        initialAppointmentTypeId={formInitialAppointmentTypeId}
        onClose={() => setIsFutureAppointmentRequestModalOpen(false)}
        onSuccess={handleFutureAppointmentRequestSuccess}
        open={isFutureAppointmentRequestModalOpen}
        person={person}
      />

      <MedicalStarterSequenceModal
        onClose={() => setIsMedicalStarterModalOpen(false)}
        onSuccess={() => setIsMedicalStarterModalOpen(false)}
        open={isMedicalStarterModalOpen}
        person={person}
      />
    </Container>
  );
};

export default memo(Scheduling);
